// src/store/reducers/productReducer.js
import { PRODUCT_ACTIONS } from '../actions/actionTypes';

const initialState = {
  products: [],
  selectedProduct: null,
  loading: false,
  error: null,
  success: null,
  meta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  filters: {
    page: 1,
    limit: 20,
    search: '',
    entityId: '',
    isActive: true,
    popular: undefined,
    new: undefined,
    minAge: '',
    maxAge: '',
    sortBy: 'order',
    sortOrder: 'asc',
  },
  features: [],
  benefits: [],
  sections: [],
  qas: [],
  pricingTiers: [],
  eligibilityCriteria: [],
  exclusions: [],
  documents: [],
  documentsMeta: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  entities: [],
  stats: null,
  uploadProgress: 0,
};

const productReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch products
    case PRODUCT_ACTIONS.FETCH_PRODUCTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case PRODUCT_ACTIONS.FETCH_PRODUCTS_SUCCESS:
      return {
        ...state,
        loading: false,
        products: action.payload.data || [],
        meta: action.payload.meta || initialState.meta,
        error: null,
      };

    case PRODUCT_ACTIONS.FETCH_PRODUCTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        products: [],
      };

    // Fetch single product
    case PRODUCT_ACTIONS.FETCH_PRODUCT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case PRODUCT_ACTIONS.FETCH_PRODUCT_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedProduct: action.payload,
        features: action.payload.features || [],
        benefits: action.payload.benefits || [],
        sections: action.payload.sections || [],
        qas: action.payload.qas || [],
        pricingTiers: action.payload.pricingTiers || [],
        eligibilityCriteria: action.payload.eligibilityCriteria || [],
        exclusions: action.payload.exclusions || [],
        documents: action.payload.documents || [],
        error: null,
      };

    case PRODUCT_ACTIONS.FETCH_PRODUCT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        selectedProduct: null,
      };

    // Create product
    case PRODUCT_ACTIONS.CREATE_PRODUCT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case PRODUCT_ACTIONS.CREATE_PRODUCT_SUCCESS:
      return {
        ...state,
        loading: false,
        products: [action.payload, ...state.products],
        success: 'Produit créé avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.CREATE_PRODUCT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Update product
    case PRODUCT_ACTIONS.UPDATE_PRODUCT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case PRODUCT_ACTIONS.UPDATE_PRODUCT_SUCCESS:
      return {
        ...state,
        loading: false,
        products: state.products.map(product =>
          product.id === action.payload.id ? action.payload : product
        ),
        selectedProduct: action.payload,
        success: 'Produit mis à jour avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.UPDATE_PRODUCT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Delete product
    case PRODUCT_ACTIONS.DELETE_PRODUCT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case PRODUCT_ACTIONS.DELETE_PRODUCT_SUCCESS:
      return {
        ...state,
        loading: false,
        products: state.products.filter(product => product.id !== action.payload.id),
        success: action.payload.message || 'Produit supprimé avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.DELETE_PRODUCT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Toggle product status
    case PRODUCT_ACTIONS.TOGGLE_PRODUCT_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case PRODUCT_ACTIONS.TOGGLE_PRODUCT_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        products: state.products.map(product =>
          product.id === action.payload.id 
            ? { ...product, isActive: action.payload.isActive }
            : product
        ),
        selectedProduct: state.selectedProduct?.id === action.payload.id
          ? { ...state.selectedProduct, isActive: action.payload.isActive }
          : state.selectedProduct,
        success: `Produit ${action.payload.isActive ? 'activé' : 'désactivé'} avec succès`,
        error: null,
      };

    case PRODUCT_ACTIONS.TOGGLE_PRODUCT_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Features
    case PRODUCT_ACTIONS.ADD_FEATURE_SUCCESS:
      return {
        ...state,
        loading: false,
        features: [...state.features, action.payload],
        selectedProduct: {
          ...state.selectedProduct,
          features: [...(state.selectedProduct?.features || []), action.payload],
        },
        success: 'Caractéristique ajoutée avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.UPDATE_FEATURE_SUCCESS:
      return {
        ...state,
        loading: false,
        features: state.features.map(f =>
          f.id === action.payload.id ? action.payload : f
        ),
        selectedProduct: {
          ...state.selectedProduct,
          features: state.selectedProduct?.features?.map(f =>
            f.id === action.payload.id ? action.payload : f
          ),
        },
        success: 'Caractéristique mise à jour avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.DELETE_FEATURE_SUCCESS:
      return {
        ...state,
        loading: false,
        features: state.features.filter(f => f.id !== action.payload.featureId),
        selectedProduct: {
          ...state.selectedProduct,
          features: state.selectedProduct?.features?.filter(f => f.id !== action.payload.featureId),
        },
        success: action.payload.message || 'Caractéristique supprimée',
        error: null,
      };

    // Benefits
    case PRODUCT_ACTIONS.ADD_BENEFIT_SUCCESS:
      return {
        ...state,
        loading: false,
        benefits: [...state.benefits, action.payload],
        selectedProduct: {
          ...state.selectedProduct,
          benefits: [...(state.selectedProduct?.benefits || []), action.payload],
        },
        success: 'Avantage ajouté avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.UPDATE_BENEFIT_SUCCESS:
      return {
        ...state,
        loading: false,
        benefits: state.benefits.map(b =>
          b.id === action.payload.id ? action.payload : b
        ),
        selectedProduct: {
          ...state.selectedProduct,
          benefits: state.selectedProduct?.benefits?.map(b =>
            b.id === action.payload.id ? action.payload : b
          ),
        },
        success: 'Avantage mis à jour avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.DELETE_BENEFIT_SUCCESS:
      return {
        ...state,
        loading: false,
        benefits: state.benefits.filter(b => b.id !== action.payload.benefitId),
        selectedProduct: {
          ...state.selectedProduct,
          benefits: state.selectedProduct?.benefits?.filter(b => b.id !== action.payload.benefitId),
        },
        success: action.payload.message || 'Avantage supprimé',
        error: null,
      };

    // Sections
    case PRODUCT_ACTIONS.ADD_SECTION_SUCCESS:
      return {
        ...state,
        loading: false,
        sections: [...state.sections, action.payload],
        selectedProduct: {
          ...state.selectedProduct,
          sections: [...(state.selectedProduct?.sections || []), action.payload],
        },
        success: 'Section ajoutée avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.UPDATE_SECTION_SUCCESS:
      return {
        ...state,
        loading: false,
        sections: state.sections.map(s =>
          s.id === action.payload.id ? action.payload : s
        ),
        selectedProduct: {
          ...state.selectedProduct,
          sections: state.selectedProduct?.sections?.map(s =>
            s.id === action.payload.id ? action.payload : s
          ),
        },
        success: 'Section mise à jour avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.DELETE_SECTION_SUCCESS:
      return {
        ...state,
        loading: false,
        sections: state.sections.filter(s => s.id !== action.payload.sectionId),
        selectedProduct: {
          ...state.selectedProduct,
          sections: state.selectedProduct?.sections?.filter(s => s.id !== action.payload.sectionId),
        },
        success: action.payload.message || 'Section supprimée',
        error: null,
      };

    // Q&A
    case PRODUCT_ACTIONS.ADD_QA_SUCCESS:
      return {
        ...state,
        loading: false,
        qas: [...state.qas, action.payload],
        selectedProduct: {
          ...state.selectedProduct,
          qas: [...(state.selectedProduct?.qas || []), action.payload],
        },
        success: 'Q&A ajoutée avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.UPDATE_QA_SUCCESS:
      return {
        ...state,
        loading: false,
        qas: state.qas.map(q =>
          q.id === action.payload.id ? action.payload : q
        ),
        selectedProduct: {
          ...state.selectedProduct,
          qas: state.selectedProduct?.qas?.map(q =>
            q.id === action.payload.id ? action.payload : q
          ),
        },
        success: 'Q&A mise à jour avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.DELETE_QA_SUCCESS:
      return {
        ...state,
        loading: false,
        qas: state.qas.filter(q => q.id !== action.payload.qaId),
        selectedProduct: {
          ...state.selectedProduct,
          qas: state.selectedProduct?.qas?.filter(q => q.id !== action.payload.qaId),
        },
        success: action.payload.message || 'Q&A supprimée',
        error: null,
      };

    // Pricing Tiers
    case PRODUCT_ACTIONS.ADD_PRICING_TIER_SUCCESS:
      return {
        ...state,
        loading: false,
        pricingTiers: [...state.pricingTiers, action.payload],
        selectedProduct: {
          ...state.selectedProduct,
          pricingTiers: [...(state.selectedProduct?.pricingTiers || []), action.payload],
        },
        success: 'Tarif ajouté avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.UPDATE_PRICING_TIER_SUCCESS:
      return {
        ...state,
        loading: false,
        pricingTiers: state.pricingTiers.map(t =>
          t.id === action.payload.id ? action.payload : t
        ),
        selectedProduct: {
          ...state.selectedProduct,
          pricingTiers: state.selectedProduct?.pricingTiers?.map(t =>
            t.id === action.payload.id ? action.payload : t
          ),
        },
        success: 'Tarif mis à jour avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.DELETE_PRICING_TIER_SUCCESS:
      return {
        ...state,
        loading: false,
        pricingTiers: state.pricingTiers.filter(t => t.id !== action.payload.tierId),
        selectedProduct: {
          ...state.selectedProduct,
          pricingTiers: state.selectedProduct?.pricingTiers?.filter(t => t.id !== action.payload.tierId),
        },
        success: action.payload.message || 'Tarif supprimé',
        error: null,
      };

    // Eligibility Criteria
    case PRODUCT_ACTIONS.ADD_ELIGIBILITY_SUCCESS:
      return {
        ...state,
        loading: false,
        eligibilityCriteria: [...state.eligibilityCriteria, action.payload],
        selectedProduct: {
          ...state.selectedProduct,
          eligibilityCriteria: [...(state.selectedProduct?.eligibilityCriteria || []), action.payload],
        },
        success: 'Critère d\'éligibilité ajouté avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.UPDATE_ELIGIBILITY_SUCCESS:
      return {
        ...state,
        loading: false,
        eligibilityCriteria: state.eligibilityCriteria.map(e =>
          e.id === action.payload.id ? action.payload : e
        ),
        selectedProduct: {
          ...state.selectedProduct,
          eligibilityCriteria: state.selectedProduct?.eligibilityCriteria?.map(e =>
            e.id === action.payload.id ? action.payload : e
          ),
        },
        success: 'Critère d\'éligibilité mis à jour avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.DELETE_ELIGIBILITY_SUCCESS:
      return {
        ...state,
        loading: false,
        eligibilityCriteria: state.eligibilityCriteria.filter(e => e.id !== action.payload.eligibilityId),
        selectedProduct: {
          ...state.selectedProduct,
          eligibilityCriteria: state.selectedProduct?.eligibilityCriteria?.filter(e => e.id !== action.payload.eligibilityId),
        },
        success: action.payload.message || 'Critère d\'éligibilité supprimé',
        error: null,
      };

    // Exclusions
    case PRODUCT_ACTIONS.ADD_EXCLUSION_SUCCESS:
      return {
        ...state,
        loading: false,
        exclusions: [...state.exclusions, action.payload],
        selectedProduct: {
          ...state.selectedProduct,
          exclusions: [...(state.selectedProduct?.exclusions || []), action.payload],
        },
        success: 'Exclusion ajoutée avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.UPDATE_EXCLUSION_SUCCESS:
      return {
        ...state,
        loading: false,
        exclusions: state.exclusions.map(e =>
          e.id === action.payload.id ? action.payload : e
        ),
        selectedProduct: {
          ...state.selectedProduct,
          exclusions: state.selectedProduct?.exclusions?.map(e =>
            e.id === action.payload.id ? action.payload : e
          ),
        },
        success: 'Exclusion mise à jour avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.DELETE_EXCLUSION_SUCCESS:
      return {
        ...state,
        loading: false,
        exclusions: state.exclusions.filter(e => e.id !== action.payload.exclusionId),
        selectedProduct: {
          ...state.selectedProduct,
          exclusions: state.selectedProduct?.exclusions?.filter(e => e.id !== action.payload.exclusionId),
        },
        success: action.payload.message || 'Exclusion supprimée',
        error: null,
      };

    // Documents
    case PRODUCT_ACTIONS.FETCH_PRODUCT_DOCUMENTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case PRODUCT_ACTIONS.FETCH_PRODUCT_DOCUMENTS_SUCCESS:
      return {
        ...state,
        loading: false,
        documents: action.payload.data || [],
        documentsMeta: action.payload.meta || initialState.documentsMeta,
        error: null,
      };

    case PRODUCT_ACTIONS.FETCH_PRODUCT_DOCUMENTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        documents: [],
      };

    case PRODUCT_ACTIONS.REQUEST_PRESIGNED_UPLOAD_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        uploadProgress: 0,
      };

    case PRODUCT_ACTIONS.CONFIRM_DOCUMENT_UPLOAD_SUCCESS:
      return {
        ...state,
        loading: false,
        documents: [action.payload, ...state.documents],
        selectedProduct: {
          ...state.selectedProduct,
          documents: [...(state.selectedProduct?.documents || []), action.payload],
        },
        uploadProgress: 100,
        success: 'Document téléchargé avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.DELETE_PRODUCT_DOCUMENT_SUCCESS:
      return {
        ...state,
        loading: false,
        documents: state.documents.filter(d => d.id !== action.payload.documentId),
        selectedProduct: {
          ...state.selectedProduct,
          documents: state.selectedProduct?.documents?.filter(d => d.id !== action.payload.documentId),
        },
        success: action.payload.message || 'Document supprimé',
        error: null,
      };

    // Entities
    case PRODUCT_ACTIONS.FETCH_ENTITIES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case PRODUCT_ACTIONS.FETCH_ENTITIES_SUCCESS:
      return {
        ...state,
        loading: false,
        entities: action.payload.data || [],
        error: null,
      };

    case PRODUCT_ACTIONS.FETCH_ENTITIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        entities: [],
      };

    // Stats
    case PRODUCT_ACTIONS.FETCH_PRODUCT_STATS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case PRODUCT_ACTIONS.FETCH_PRODUCT_STATS_SUCCESS:
      return {
        ...state,
        loading: false,
        stats: action.payload,
        error: null,
      };

    case PRODUCT_ACTIONS.FETCH_PRODUCT_STATS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        stats: null,
      };

    // Bulk delete
    case PRODUCT_ACTIONS.BULK_DELETE_PRODUCTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case PRODUCT_ACTIONS.BULK_DELETE_PRODUCTS_SUCCESS:
      return {
        ...state,
        loading: false,
        products: state.products.filter(p => !action.payload.ids.includes(p.id)),
        success: action.payload.message || 'Produits supprimés avec succès',
        error: null,
      };

    case PRODUCT_ACTIONS.BULK_DELETE_PRODUCTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Set filters
    case PRODUCT_ACTIONS.SET_PRODUCT_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    // Clear states
    case PRODUCT_ACTIONS.CLEAR_PRODUCT_ERROR:
      return {
        ...state,
        error: null,
      };

    case PRODUCT_ACTIONS.CLEAR_PRODUCT_SUCCESS:
      return {
        ...state,
        success: null,
      };

    case PRODUCT_ACTIONS.CLEAR_SELECTED_PRODUCT:
      return {
        ...state,
        selectedProduct: null,
        features: [],
        benefits: [],
        sections: [],
        qas: [],
        pricingTiers: [],
        eligibilityCriteria: [],
        exclusions: [],
        documents: [],
      };

    default:
      return state;
  }
};

export default productReducer;